import type { TeamWith } from "@redotech/account-common/base-team";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import type { OutboundLabelSettings } from "@redotech/redo-fulfillment-settings/outbound-label-settings";
import { Flex } from "@redotech/redo-web/flex";
import { toMoney } from "@redotech/redo-web/fx/to-money";
import {
  ItemTableHeader,
  ProductListRow,
} from "@redotech/redo-web/product-list-row";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export interface ItemSection {
  header: string;
  items: FulfillmentOrderLineItem[];
}

export const SectionedItemsTable = memo(function SectionedItemsTable({
  sections,
  team,
  disableTooltip = false,
  hideHeader = false,
}: {
  sections: ItemSection[];
  team: TeamWith<{ outboundLabels: OutboundLabelSettings }> | undefined;
  disableTooltip?: boolean;
  hideHeader?: boolean;
}) {
  const showCustomAttributes =
    !!team?.settings?.outboundLabels?.degroupItemsByCustomAttributes;
  const hideUnderscoreCustomAttributes =
    !!team?.settings?.outboundLabels?.hideUnderscoreCustomAttributes;

  return (
    <Flex
      borderColor="primary"
      borderRadius="none"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
    >
      {!hideHeader && <ItemTableHeader flatTopCorners={false} />}
      <Flex dir="column" flex="1" gap="none">
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex}>
            <Flex
              bgColor="secondary"
              borderBottomWidth="1px"
              borderColor="secondary"
              borderStyle="solid"
              px="xl"
              py="lg"
              w="full"
            >
              <Text fontSize="xxs" fontWeight="medium" textColor="secondary">
                {section.header}
              </Text>
            </Flex>
            {section.items
              .filter((item) => item.quantity > 0)
              .map((item, itemIndex) => (
                <Flex
                  borderBottomWidth="1px"
                  borderColor="secondary"
                  borderStyle="solid"
                  gap="none"
                  key={`${sectionIndex}-${itemIndex}`}
                  w="full"
                >
                  <ProductListRow
                    customAttributes={
                      showCustomAttributes
                        ? hideUnderscoreCustomAttributes
                          ? item.customAttributes?.filter(
                              (attr) => !attr.key.startsWith("_"),
                            )
                          : item.customAttributes
                        : undefined
                    }
                    disableTooltip={disableTooltip}
                    imageUrl={item.imageUrl}
                    price={item.unitDiscountedPrice.amount}
                    priceMoney={toMoney(item.unitDiscountedPrice)}
                    quantity={item.quantity}
                    sku={item.sku}
                    title={item.productTitle || item.title}
                    variantTitle={item.variantTitle}
                  />
                </Flex>
              ))}
          </div>
        ))}
      </Flex>
    </Flex>
  );
});
